/**
 * CR114: Asset Studio Page
 * CR314: Added image upload from desktop
 */

import { useState, useMemo } from "react";
import { getAssetImageSrc } from "@/lib/assets/getAppAssetPublicUrl";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Palette,
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Copy,
  Archive,
  RotateCcw,
  Trash2,
  Sparkles,
  Grid3X3,
  Layers,
  Upload,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useAppAssets } from "@/hooks/useAppAssets";
import { useAssetUpload } from "@/hooks/useAssetUpload";
import { AssetEditorDialog } from "@/components/admin/asset-studio/AssetEditorDialog";
import { AssetPreviewCard } from "@/components/admin/asset-studio/AssetPreviewCard";
import { SeedAssetsDialog } from "@/components/admin/asset-studio/SeedAssetsDialog";
import { AssetUploadDropzone } from "@/components/admin/asset-studio/AssetUploadDropzone";
import type { AppAsset, AssetType, AssetStatus } from "@/lib/asset-studio/types";
import { ASSET_TYPE_LABELS } from "@/lib/asset-studio/types";

export default function AssetStudio() {
  const [statusFilter, setStatusFilter] = useState<AssetStatus | "all">("active");
  const [typeFilter, setTypeFilter] = useState<AssetType | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  
  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<AppAsset | null>(null);
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const { 
    assets, 
    loading, 
    saving,
    createAsset,
    updateAsset,
    archiveAsset,
    restoreAsset,
    duplicateAsset,
    deleteAsset,
    refetch,
  } = useAppAssets({
    status: statusFilter === "all" ? undefined : statusFilter,
    type: typeFilter === "all" ? undefined : typeFilter,
  });

  // CR314: Upload hook
  const {
    uploading,
    uploadingFiles,
    uploadFile,
    uploadFiles,
    clearUploadingFiles,
  } = useAssetUpload({
    onSuccess: () => {
      refetch();
    },
  });

  // Filtered assets
  const filteredAssets = useMemo(() => {
    if (!searchQuery.trim()) return assets;
    const q = searchQuery.toLowerCase();
    return assets.filter(a => 
      a.name.toLowerCase().includes(q) || 
      a.tags.some(t => t.toLowerCase().includes(q))
    );
  }, [assets, searchQuery]);

  // Stats
  const stats = useMemo(() => ({
    total: assets.length,
    active: assets.filter(a => a.status === "active").length,
    archived: assets.filter(a => a.status === "archived").length,
    byType: Object.fromEntries(
      Object.keys(ASSET_TYPE_LABELS).map(type => [
        type,
        assets.filter(a => a.type === type).length,
      ])
    ),
  }), [assets]);

  // Handlers
  const handleCreate = () => {
    setEditingAsset(null);
    setEditorOpen(true);
  };

  const handleEdit = (asset: AppAsset) => {
    setEditingAsset(asset);
    setEditorOpen(true);
  };

  const handleDuplicate = async (id: string) => {
    await duplicateAsset(id);
  };

  const handleArchive = async (id: string) => {
    await archiveAsset(id);
  };

  const handleRestore = async (id: string) => {
    await restoreAsset(id);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Permanently delete this asset? This cannot be undone.")) return;
    await deleteAsset(id);
  };

  const handleEditorSave = async (data: { 
    name: string; 
    type: AssetType; 
    svg: string; 
    is_tileable: boolean;
    default_tint: string | null;
    tint_mode: "none" | "fill" | "stroke" | "both";
    tags: string[];
    uploadedFile?: File;
  }) => {
    if (editingAsset) {
      const success = await updateAsset(editingAsset.id, data);
      if (success) setEditorOpen(false);
    } else if (data.uploadedFile) {
      // CR314: Upload file flow
      const result = await uploadFile(data.uploadedFile, {
        name: data.name,
        type: data.type,
        tags: data.tags,
      });
      if (result.success) {
        refetch();
        setEditorOpen(false);
      }
    } else {
      const created = await createAsset(data);
      if (created) setEditorOpen(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Palette className="h-6 w-6 text-primary" />
              Asset Studio
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage visual assets for Theme Labs skins and overlays
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setSeedDialogOpen(true)}>
              <Sparkles className="h-4 w-4 mr-2" />
              Seed Placeholders
            </Button>
            <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Image
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              New Asset
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-muted-foreground">Total Assets</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-primary">{stats.active}</div>
              <div className="text-sm text-muted-foreground">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-muted-foreground">{stats.archived}</div>
              <div className="text-sm text-muted-foreground">Archived</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{Object.keys(ASSET_TYPE_LABELS).length}</div>
              <div className="text-sm text-muted-foreground">Asset Types</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as AssetType | "all")}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as AssetStatus | "all")}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1 ml-auto">
            <Button
              variant={viewMode === "table" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("table")}
            >
              <Layers className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("grid")}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        {filteredAssets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Palette className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground">
                {searchQuery ? "No assets match your search" : "No assets yet"}
              </p>
              <Button variant="outline" className="mt-4" onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Asset
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredAssets.map((asset) => (
              <AssetPreviewCard
                key={asset.id}
                asset={asset}
                onEdit={() => handleEdit(asset)}
                onDuplicate={() => handleDuplicate(asset.id)}
                onArchive={() => handleArchive(asset.id)}
                onRestore={() => handleRestore(asset.id)}
                onDelete={() => handleDelete(asset.id)}
              />
            ))}
          </div>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Preview</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAssets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell>
                      {/* CR201/CR282: Use img with resolved URL */}
                      <div className="w-10 h-10 rounded bg-muted flex items-center justify-center overflow-hidden">
                        <img 
                          src={getAssetImageSrc(asset) || ""}
                          alt={asset.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{asset.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {ASSET_TYPE_LABELS[asset.type]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {asset.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-[10px]">
                            {tag}
                          </Badge>
                        ))}
                        {asset.tags.length > 3 && (
                          <Badge variant="secondary" className="text-[10px]">
                            +{asset.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={asset.status === "active" ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {asset.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(asset.updated_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(asset)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(asset.id)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {asset.status === "active" ? (
                            <DropdownMenuItem onClick={() => handleArchive(asset.id)}>
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleRestore(asset.id)}>
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Restore
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(asset.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Editor Dialog */}
      <AssetEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        asset={editingAsset}
        onSave={handleEditorSave}
        saving={saving}
      />

      {/* Seed Dialog */}
      <SeedAssetsDialog
        open={seedDialogOpen}
        onOpenChange={setSeedDialogOpen}
      />

      {/* CR314: Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(open) => {
          setUploadDialogOpen(open);
          if (!open) clearUploadingFiles();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Upload Images
            </DialogTitle>
            <DialogDescription>
              Upload PNG, JPG, WebP, or SVG files to your asset library.
            </DialogDescription>
          </DialogHeader>
          <AssetUploadDropzone
            onUpload={(files, overrides) => uploadFiles(files, overrides)}
            uploading={uploading}
            uploadingFiles={uploadingFiles}
          />
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setUploadDialogOpen(false);
                clearUploadingFiles();
              }}
            >
              {uploadingFiles.some((f) => f.status === "success")
                ? "Done"
                : "Close"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
